import { DomHelper, SchedulerPro } from '@bryntum/schedulerpro';

import { ScheduleConstants } from '@/common/bryntum/constants';
import { useOverlappingMilestones } from '@/core/milestones/overlappingMilestones';
import { useWbsSectionStore } from '@/features/projectStructure/store/sectionStore';
import { getSectionAncestors } from '@/features/projectStructure/utils/sectionTreeUtils';
import { stackEventsInResourceIfMilestonesOverlapOnDate } from '@/features/schedule/bryntum/resources/layout';
import {
  expandAncestors,
  scrollToDate,
  scrollToResource,
} from '@/features/schedule/bryntum/schedulerInteractions';
import { useScheduleViewStore } from '@/features/scheduleViews/scheduleViewStore';
import { asyncTimeout } from '@/helpers/utils/functions';
import { NodeName } from '@/repositories/utils/cache';
import { useLoggingService } from '@/services/logging/composable';
import {
  getAdjustedPopupMouseEvent,
  getByEventId,
} from '@/services/store/schedule/config/listeners/events';
import { getMainResourceId } from '@/services/store/schedule/parsers/base';
import { ScheduleStore } from '@/services/store/schedule/store';
import { SchedulerResource } from '@/services/store/schedule/types/types';
import { useSchedulePopupStore } from '@/services/store/schedulePopup/store';
import { SchedulerPopupComponent } from '@/services/store/schedulePopup/types';
import { AnalyticsEvent } from '@/utils/analyticsEvents/categories';

import { CollisionViewedEvent } from '../collisionAnalytics';
import { useCollisionStore } from '../collisionStore';
import { getCollisionsByTarget, getCollisionsOfDate, isCollisionFiltered } from '../collisionUtils';
import { Collision } from '../types';

export async function onClickCollision(
  scheduler: SchedulerPro,
  scheduleStore: ScheduleStore,
  collisionId: string,
): Promise<void> {
  const collisionStore = useCollisionStore();
  const schedulePopupStore = useSchedulePopupStore();

  const collision = collisionStore.collisions.get(collisionId);
  if (!collision) {
    return;
  }

  const collisionsOnSameDateForSameTarget = getCollisionsByTarget(
    getCollisionsOfDate(collisionStore.collisions, collision?.date),
  );
  if (collisionsOnSameDateForSameTarget.size > 1) {
    schedulePopupStore.closePopup();
    const element = document.querySelector(`[data-id="${collision.id}"]`) as HTMLElement | null;
    if (!element) return;
    const event = getAdjustedPopupMouseEvent(new MouseEvent('click'), element);
    setTimeout(() => {
      schedulePopupStore.openPopup({
        component: SchedulerPopupComponent.SCHEDULE_ALERT_JUMP_LIST,
        payload: {
          mouseEvent: new MouseEvent('click', {
            ...event,
            // Reduce width of tooltip to center it. Ideally the popup should use useGloballyPositionedOverlay,
            // but it is not feasible to change it now, so we hardcode the width (- tooltip width + half of event width)
            clientX: event.clientX - 150 + 15,
            clientY: event.clientY,
          }),
          date: collision.date,
        },
      });
    });
    return;
  }

  if (isCollisionFiltered(collision)) return;

  scrollCollisionTargetIntoView(
    scheduler,
    scheduleStore,
    collision,
    new CollisionViewedEvent({
      type: collision.toType === NodeName.MILESTONE ? 'milestone' : 'order',
      source: 'row',
    }),
  );
}

export const scrollCollisionTargetIntoView = async (
  scheduler: SchedulerPro,
  scheduleStore: ScheduleStore,
  collision: Pick<Collision, 'toId' | 'wbsSectionId' | 'type' | 'date'>,
  trackingEvent: AnalyticsEvent,
  scrollToCollisionDate = false,
): Promise<void> => {
  const scheduleViewStore = useScheduleViewStore();
  const wbsSectionStore = useWbsSectionStore();
  const schedulePopupStore = useSchedulePopupStore();
  const loggingService = useLoggingService();
  const overlappingMilestones = useOverlappingMilestones();

  const resourceId = collision.wbsSectionId ?? getMainResourceId();

  schedulePopupStore.closePopup();
  scheduleStore.closeSidebar();

  // wait a little longer until popups and sidebar are closed before opening the popup to make it more stable
  await asyncTimeout(() => Promise.resolve(), ScheduleConstants.SIDEBAR_TOGGLE_DELAY);

  if (resourceId !== getMainResourceId()) {
    const ancestors = getSectionAncestors(wbsSectionStore.wbsSections, resourceId);
    // Add timeout, as re-rendering the resources after expanding might take longer than the expand itself
    await asyncTimeout(
      () => expandAncestors(ancestors, scheduler, scheduleStore, scheduleViewStore),
      100,
    );
  }

  const targetScrollDate = scrollToCollisionDate ? collision.date : undefined;
  await scrollIntoView(scheduler, resourceId, targetScrollDate);
  stackEventsInResourceIfMilestonesOverlapOnDate({
    resource: scheduler.resourceStore.getById(resourceId) as SchedulerResource,
    date: collision.date,
    scheduleStore,
    loggingService,
    overlappingMilestones,
  });
  // wait a little longer until expand and scroll is finished before opening the popup to make it more stable
  // and to prevent the automatic closing of the popup
  openPopup(schedulePopupStore, collision, ScheduleConstants.POPUP_TOGGLE_DELAY);
  loggingService.trackEvent(trackingEvent);
};

const scrollIntoView = async (
  scheduler: SchedulerPro,
  resourceId: string,
  targetScrollDate: Date | undefined,
): Promise<void> => {
  const visibleDate = scheduler.visibleDateRange.startDate
    ? new Date(scheduler.visibleDateRange.startDate)
    : undefined;
  const resourceScrollPromise = scrollToResource({ scheduler, resourceId, forceScroll: true });

  if (targetScrollDate) {
    await scrollToDate({ scheduler, dates: [targetScrollDate], forceScroll: true });
  } else {
    // Hack to prevent bryntum from scrolling to a random date
    if (visibleDate) {
      await scrollToDate({ scheduler, dates: [visibleDate], position: 'start', forceScroll: true });
    }
  }
  await resourceScrollPromise;
};

const openPopup = (
  schedulePopupStore: ReturnType<typeof useSchedulePopupStore>,
  collision: Pick<Collision, 'toId' | 'wbsSectionId' | 'type' | 'date'>,
  timeout: number,
): void => {
  setTimeout(() => {
    // If the target element is not visible, we don't open the popup
    const toElement = getByEventId(collision.toId);
    if (!toElement || !DomHelper.isVisible(toElement)) return;

    schedulePopupStore.openPopup({
      component: SchedulerPopupComponent.COLLISION_DETAILS,
      payload: {
        mouseEvent: getAdjustedPopupMouseEvent(
          new MouseEvent('click'),
          toElement,
          collision.type === 'start' ? 'left' : 'right',
        ),
        toId: collision.toId,
        date: collision.date,
      },
    });
  }, timeout);
};
